import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { stringify } from 'qs';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';

import { config } from '../../../../config';
import { TokenService } from '../../services';
import { Payment } from '../payments';
import { Debt, Activity, Paginated } from './debt.model';
import { Offer, Plan } from './plan.model';

@Injectable()
export class DebtService {
  constructor(private http: HttpClient, private token: TokenService) { }

  get<T = Debt>(terms?: object, page?: number, format?: string): Observable<Paginated<T>> {
    return this.http
      .get<Paginated<T>>(`${config.apiLocation}/v1/debts?${stringify({ terms, page, format })}`)
      .map(res => res);
  }

  getForClient<T = Debt>(
    id: string,
    terms?: object,
    page?: number,
    format?: string,
    include?: string[]
  ): Observable<Paginated<T>> {
    return this.http
      .get<Paginated<T>>(`${config.apiLocation}/v1/clients/${id}/debts?${stringify({ terms, page, format, include })}`);
  }

  getById(id): Observable<Debt> {
    return this.http
      .get<{ data: Debt }>(`${config.apiLocation}/v1/debts/${id}`)
      .map(res => res.data);
  }

  chaseCommission(id, data) {
    return this.http.post<void>(`${config.apiLocation}/v1/debts/${id}/chase-commission`, { data });
  }

  create(debt: Debt): Observable<Debt> {
    return this.http
      .post<{ data: Debt }>(`${config.apiLocation}/v1/debts`, debt)
      .map(res => res.data);
  }

  update(debt: RecursivePartial<Debt>): Observable<Debt> {
    return this.http
      .patch<{ data: Debt }>(`${config.apiLocation}/v1/debts/${debt.id}`, debt)
      .map(res => res.data)
      .catch((err: HttpErrorResponse) => {
        console.error('An error occurred:', err.error);
        return Observable.throw(err.statusText);
      });
  }

  stopChasing(id, data) {
    return this.http.post<{ data: Debt }>(`${config.apiLocation}/v1/debts/${id}/stop-chasing`, data);
  }

  cancel(id, data) {
    return this.http.post<{ data: Debt }>(`${config.apiLocation}/v1/debts/${id}/cancel`, data);
  }

  requestCancellation(id, data) {
    return this.http.post<{ data: Debt }>(`${config.apiLocation}/v1/debts/${id}/request-cancel`, data);
  }

  open(id) {
    return this.http.post<{ data: Debt }>(`${config.apiLocation}/v1/debts/${id}/open`, {});
  }

  reopen(id) {
    return this.http.get<{ data: Debt }>(`${config.apiLocation}/v1/debts/${id}/reopen`);
  }

  createSlod(debt_id: string, data: {}): Observable<Debt> {
    return this.http
      .post<{ data: Debt }>(`${config.apiLocation}/v1/debts/${debt_id}/slods`, data)
      .map(res => res.data);
  }

  checkPlan(debt: Debt, debt_amount: number, amount: number, frequency: number) {
    return this.http.post<void>(`${config.apiLocation}/v1/debts/${debt.id}/plans/check`, {
      debt_amount,
      amount,
      frequency
    });
  }

  offers(debt: Debt) {
    return this.http
      .get<{ data: Offer[] }>(`${config.apiLocation}/v1/debts/${debt.id}/plans/offers`)
      .map(res => res.data);
  }

  createNote(debt: Debt, note) {
    return this.http
      .post<{ data: any }>(`${config.apiLocation}/v1/debts/${debt.id}/notes`, note)
      .map(res => res.data);
  }

  cancelPlan(debt: Debt) {
    return this.http.delete<void>(`${config.apiLocation}/v1/debts/${debt.id}/plans/${debt.plan.id}`);
  }

  createPayment(debt: Debt, payment: Payment) {
    return this.http
      .post<{ data: Payment }>(`${config.apiLocation}/v1/debts/${debt.id}/payments`, payment)
      .map(res => res.data);
  }

  createReference(debt: Debt, amount: number) {
    return this.http
      .post<{ data: Debt }>(`${config.apiLocation}/v1/debts/${debt.id}/payments/createReference`, {
        amount
      })
      .toPromise();
  }

  createInstantPayment(debt: Debt, payment_reference: string, amount: number) {
    return this.http
      .post<{ data: Payment }>(`${config.apiLocation}/v1/debts/${debt.id}/payments/instant`, {
        payment_reference,
        amount,
      })
      .map(res => res.data);
  }

  createInstantPaymentDeclined(debt: Debt, payment_reference: string, amount: number) {
    return this.http
      .post<{ data: Payment }>(`${config.apiLocation}/v1/debts/${debt.id}/payments/declined`, {
        payment_reference,
        amount,
      })
      .map(res => res.data);
  }

  createIntroducedPayment(debt: Debt, data: any) {
    return this.http
      .post<{ data: Debt }>(`${config.apiLocation}/v1/debts/${debt.id}/payments/introduced`, data)
      .map(res => res.data);
  }

  createPlan(debt: Debt, payment: Payment) {
    return this.http
      .post<{ data: Plan }>(`${config.apiLocation}/v1/debts/${debt.id}/plans`, payment)
      .map(res => res.data);
  }

  zeroBalanceUrl(debt: Debt): Observable<string> {
    return this.token
      .getAccessToken()
      .map(token => `${config.apiLocation}/v1/debts/${debt.id}/zero-balance?token=${token}`);
  }

  addActivity(id: string, body: object): Observable<Activity> {
    return this.http
      .post<{ data: Activity }>(`${config.apiLocation}/v1/debts/${id}/add-activity`, body)
      .map(res => res.data);
  }

  /**
   * Mark a debt as finished following up
   *
   * @param Payment payment
   */
  followedUp(debt: Debt) {
    return this.http.get(`${config.apiLocation}/v1/debts/${debt.id}/followed-up`);
  }

  delete(debtId: string) {
    return this.http.delete<void>(`${config.apiLocation}/v1/debts/${debtId}`, {});
  }
}



// WEBPACK FOOTER //
// ./src/app/core/data/debts/debt.service.ts